import React from "react";
import { Link } from "react-router-dom";
import ChatSetupSubNavigation from "./ChatSetupSubNavigation";
import {
  NEXT_LAUNCHPAD_TARGETS,
  isNextLaunchpadEnabledForUser,
  isNextLaunchpadRefinementsEnabledForUser,
  openNextLaunchpad,
} from "../../../util/nextLaunchpad";
import {
  CONVERSATION_ITEM_CONFIG,
  CONVERSATION_ITEM_ICONS,
} from "../../../config/menuConfig";

const AI_LAUNCHPAD_COMPONENT = "__next_ai_launchpad__";
const AI_RECEPTIONIST_REFINEMENTS_COMPONENT = "__next_ai_receptionist_refinements__";


class ConversationNavItems extends React.Component {
  constructor(props) {
    super(props);
    this.state = { toggleMenu: false, active: "", nextLaunchpadLoadingTarget: "" };
  }

  toggleNavMenu = (event, tab) => {
    event.preventDefault();
    this.setState({ active: tab, toggleMenu: !this.state.toggleMenu });
  };

  openLaunchpadTarget = async (event, target) => {
    event.preventDefault();
    const canOpen =
      target === NEXT_LAUNCHPAD_TARGETS.PARTNER_REFINEMENTS
        ? isNextLaunchpadRefinementsEnabledForUser({ user: this.props.user })
        : isNextLaunchpadEnabledForUser({ user: this.props.user });
    if (!canOpen) return;
    this.setState({ nextLaunchpadLoadingTarget: target });
    try {
      await openNextLaunchpad({ user: this.props.user, target });
    } catch (err) {
      console.error("Error opening AI Launchpad:", err);
      alert("AI Launchpad is not available right now. Please try again.");
    } finally {
      this.setState({ nextLaunchpadLoadingTarget: "" });
    }
  };

  // Compute visibility for a conversation item using menuConfig rules.
  // Always returns true/false — never removes the node from the DOM.
  isVisible = (comp) => {
    const { user, isPLG, twilioStatus } = this.props;
    const modules = user.totalAvailableModules || [];
    const cfg = CONVERSATION_ITEM_CONFIG[comp];
    if (!cfg) return false;
    if (cfg.hiddenForPLG && isPLG) return false;
    if (cfg.requiresTwilio && !twilioStatus) return false;
    if (cfg.requiresVocera && user.is_vocera !== "Yes") return false;
    if (cfg.module && !modules.includes(cfg.module)) return false;
    return true;
  };

  isActive = (comp) => {
    const { location } = this.props;
    if (comp === "/chat-dashboard") return location.pathname === "/chat-dashboard" || location.pathname === "/";
    if (comp === "/botbuilder")     return location.pathname.includes("/botbuilder") || location.pathname.includes("/automation");
    if (comp === "/installation")   return location.pathname.includes("installation");
    return location.pathname === comp;
  };

  render() {
    const { user, location } = this.props;
    const { toggleMenu, active, nextLaunchpadLoadingTarget } = this.state;
    const canOpenAiLaunchpad = isNextLaunchpadEnabledForUser({ user });
    const canOpenAiReceptionistRefinements = isNextLaunchpadRefinementsEnabledForUser({ user });

    const conversationMenu = (user.leftBarAccessList || []).find(
      (item) => item.key === "conversation" || (item.title || "").toLowerCase() === "conversations"
    );
    const baseChildren = conversationMenu ? conversationMenu.childElement || [] : [];
    const injected = [];
    if (canOpenAiReceptionistRefinements && !baseChildren.some((c) => c.component === AI_RECEPTIONIST_REFINEMENTS_COMPONENT)) {
      injected.push({ title: "Review My Setup", component: AI_RECEPTIONIST_REFINEMENTS_COMPONENT });
    }
    const children = [...baseChildren, ...injected];

    return children.map((child) => {
      const comp = child.component;
      const icon = CONVERSATION_ITEM_ICONS[comp];
      const visible = this.isVisible(comp);
      const activeClass = this.isActive(comp) ? "kt-menu__item--active linear-gradient-a" : "";

      // ── AI Launchpad (injected by addAiLaunchpadMenuItems) ──────────────────
      if (comp === AI_LAUNCHPAD_COMPONENT) {
        return (
          <li
            key="ai-launchpad"
            style={{ display: canOpenAiLaunchpad ? "" : "none" }}
            className="kt-menu__item kt-menu__item--submenu kt-menu__item--rel"
            aria-haspopup="true"
          >
            <a
              href="#"
              className="kt-menu__link"
              onClick={(event) => this.openLaunchpadTarget(event, NEXT_LAUNCHPAD_TARGETS.STANDARD_LAUNCHPAD)}
            >
              <span className="kt-menu__link-text">
                <i className="fa fa-magic" aria-hidden="true"></i>
                {nextLaunchpadLoadingTarget === NEXT_LAUNCHPAD_TARGETS.STANDARD_LAUNCHPAD
                  ? "Opening AI Launchpad..."
                  : "AI Launchpad"}
              </span>
            </a>
          </li>
        );
      }

      // ── Review My Setup (injected by addAiLaunchpadMenuItems) ───────────────
      if (comp === AI_RECEPTIONIST_REFINEMENTS_COMPONENT) {
        return (
          <li
            key="review-my-setup"
            style={{ display: canOpenAiReceptionistRefinements ? "" : "none" }}
            className="kt-menu__item kt-menu__item--submenu kt-menu__item--rel"
            aria-haspopup="true"
          >
            <a
              href="#"
              className="kt-menu__link"
              onClick={(event) => this.openLaunchpadTarget(event, NEXT_LAUNCHPAD_TARGETS.PARTNER_REFINEMENTS)}
            >
              <span className="kt-menu__link-text">
                <i className="fa fa-pencil-square-o" aria-hidden="true"></i>
                {nextLaunchpadLoadingTarget === NEXT_LAUNCHPAD_TARGETS.PARTNER_REFINEMENTS
                  ? "Opening your setup..."
                  : "Review My Setup"}
              </span>
            </a>
          </li>
        );
      }

      // ── Chat Setup (needs submenu toggle + ChatSetupSubNavigation) ──────────
      if (comp === "/chat-setup") {
        return (
          <li
            key={comp}
            style={{ display: visible ? "" : "none" }}
            className={`kt-menu__item ${activeClass} kt-menu__item--submenu kt-menu__item--rel`}
            aria-haspopup="true"
            data-ktmenu-submenu-toggle="click"
          >
            {location.pathname === "/chat-setup" ? (
              <a
                href="#"
                onClick={(event) => this.toggleNavMenu(event, "chatsetup")}
                className="kt-menu__link kt-menu__toggle"
              >
                <span className="kt-menu__link-text">
                  <i className={icon} aria-hidden="true"></i>
                  {child.title}
                </span>
                <i className="kt-menu__hor-arrow la la-angle-down" />
              </a>
            ) : (
              <Link to="/chat-setup" className="kt-menu__link">
                <span className="kt-menu__link-text">
                  <i className={icon} aria-hidden="true"></i>
                  {child.title}
                </span>
                <i className="kt-menu__hor-arrow la la-angle-down" />
              </Link>
            )}
            <ChatSetupSubNavigation display={toggleMenu && active === "chatsetup"} />
          </li>
        );
      }

      // ── All other standard items ─────────────────────────────────────────────
      if (CONVERSATION_ITEM_CONFIG[comp]) {
        return (
          <li
            key={comp}
            style={{ display: visible ? "" : "none" }}
            className={`kt-menu__item ${activeClass} kt-menu__item--submenu kt-menu__item--rel`}
            aria-haspopup="true"
          >
            <Link to={comp} className="kt-menu__link">
              <span className="kt-menu__link-text">
                {icon && <i className={icon} aria-hidden="true"></i>}
                {child.title}
              </span>
            </Link>
          </li>
        );
      }

      // ── Unknown — stable hidden placeholder so array indices never shift ─────
      return <li key={comp || child.title} style={{ display: "none" }} aria-hidden="true" />;
    });
  }
}

export default ConversationNavItems;
